<template>
  <v-row v-if="showEnable" no-gutters>
    <v-col>
      <v-select
        v-model="fieldValue"
        :label="showLabel"
        :disabled="isDisconnected"
        :items="selectOptions"
        item-value="value"
        item-text="text"
        :loading="isLoading"
        :readonly="isReadOnly"
        :variant="variant"
        :error-messages="error_msg"
        @update:model-value="writeValue"
      />
    </v-col>
    <ConfigInputButtons
      :default-value="defaultValue"
      :is-loading="isLoading"
      :is-reload-disabled="isReloadDisabled"
      :is-set-default-disabled="isSetDefaultDisabled"
      :parameter="props.parameter"
      :show-buttons="props.showButtons"
      @reload-value="reloadValue"
      @set-default="setDefault"
    />
  </v-row>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import ConfigInputButtons from "../ConfigInputButtons.vue";
import { useByteUtils } from "@/composables/useByteUtils";
import { useDelay } from "@/composables/useDelay";
import { ParamField } from "../../enums/ParamField";
import { PortState } from "@/enums/PortState";
import { useModbusStore } from "@/stores/useModbusStore";
import { ModbusParam } from "@/types/ModbusParam";
import { TimerId } from "@/types/TimerId";

const { delay } = useDelay();
const modbusStore = useModbusStore();
const { bytes_to_uint32, uint16_to_bytes, uint32_to_bytes } = useByteUtils();

const props = defineProps<{
  parameter: ModbusParam;
  showButtons?: boolean;
  showGroup?: boolean;
  modbusReload?: boolean;
}>();

let write_count = 0;
let write_value_array: number[] = [];
let reloadCount = 0;
let reload_addr: string = "0";
let reload_len = 0;

const modbusReqTimer = ref<TimerId>(0);
const fieldValue = ref();
const isInitialized = ref(false);

const isDefault = computed(() => {
  return defaultValue.value == fieldValue.value;
});

const isReloadDisabled = computed<boolean>(() => false);

const isSetDefaultDisabled = computed<boolean>(
  () => isDefault.value || isReadOnly.value,
);

const variant = computed(() => {
  return isReadOnly.value ? undefined : "underlined";
});

const showLabel = computed(() => {
  if (props.showGroup) {
    if (props.parameter.sub_group === "0" || !props.parameter.sub_group) {
      return `${props.parameter.name} ( ${props.parameter.group} ) `;
    } else {
      return `${props.parameter.name} ( ${props.parameter.group} / ${props.parameter.sub_group} ) `;
    }
  } else {
    return props.parameter.name;
  }
});

const showEnable = computed(() => {
  return (
    props.parameter.input_type === "dropdown" &&
    modbusStore.isParamUserReadable(props.parameter)
  );
});

const isLoading = computed(() => {
  if (
    props.parameter[ParamField.mbusAddress] in modbusStore.modbusAddrReadBusy
  ) {
    return modbusStore.modbusAddrReadBusy[
      props.parameter[ParamField.mbusAddress]
    ];
  } else {
    return false;
  }
});

const error_msg = computed(() => {
  return modbusStore.modbusAddrErrMsg[props.parameter[ParamField.mbusAddress]];
});

const defaultValue = computed<string>(() => {
  return props.parameter[ParamField.default];
});

const isDisconnected = computed<boolean>(
  () => modbusStore.portState === PortState.disconnected,
);

const isReadOnly = computed(
  () => !modbusStore.isParamUserWriteable(props.parameter),
);

const options = computed(() => {
  if (props.parameter[ParamField.inputInfo] === "ref_unit_type") {
    const unitItems = modbusStore.guiUnits.filter(
      (unitItem) => unitItem.unit_type == ref_unit_type.value,
    );
    const systems: string[] = [];
    unitItems.forEach((unitItem) => {
      if (!systems.find((system: string) => system === unitItem.system)) {
        systems.push(unitItem.system);
      }
    });

    unitItems.sort(function (a, b) {
      if (a.system === b.system) {
        return 0;
      } else {
        return systems.indexOf(a.system) - systems.indexOf(b.system);
      }
    });

    return unitItems
      ? unitItems.map((r) => {
          return r["Symbol"] + " - " + r.name;
        })
      : [];
  } else if (props.parameter[ParamField.unitType] === "default_unit") {
    const unitItems = modbusStore.guiUnits.filter(
      (guiUnit) => guiUnit.unit_type == props.parameter[ParamField.unit],
    );
    const systems: string[] = [];
    unitItems.forEach((item) => {
      if (!systems.find((r) => r == item.system)) {
        systems.push(item.system);
      }
    });

    unitItems.sort(function (a, b) {
      if (a.system === b.system) {
        return 0;
      } else {
        return systems.indexOf(a.system) - systems.indexOf(b.system);
      }
    });

    return unitItems
      ? unitItems.map((r) => {
          return r["Symbol"] + " - " + r.name;
        })
      : [];
  } else {
    return props.parameter[ParamField.inputInfo].split("|");
  }
});

const selectOptions = computed(() =>
  options.value.map((option) => {
    if (option.toString().search("=") > 0) {
      const title_value = option.toString().split("=");
      return { title: title_value[0], value: parseInt(title_value[1]) };
    } else {
      return { title: option, value: options.value.indexOf(option) };
    }
  }),
);

const ref_unit_type = computed(() => {
  let ref_unit_type = "";
  if (props.parameter[ParamField.inputInfo] === "ref_unit_type") {
    if (props.parameter[ParamField.unitType] === "ref_unit_type") {
      const param = modbusStore.params.find(
        (param) =>
          param[ParamField.mbusAddress] ==
          props.parameter[ParamField.unit].toString(),
      );
      ref_unit_type = param
        ? param.input_info.split("|")[
            modbusStore.modbusValues[props.parameter[ParamField.unit]]
          ]
        : "xxxxxxxxxxx";
    } else {
      ref_unit_type = props.parameter[ParamField.unitType] ?? "";
    }
  }
  return ref_unit_type;
});

const modbusAddrWriteBusy = computed(() => {
  return modbusStore.modbusAddrWriteBusy[
    props.parameter[ParamField.mbusAddress]
  ];
});

const modbusAddrReadBusy = computed(() => {
  return modbusStore.modbusAddrReadBusy[
    props.parameter[ParamField.mbusAddress]
  ];
});

async function updateValue() {
  if (props.parameter[ParamField.inputType] === "dropdown") {
    if (props.parameter[ParamField.inputInfo] === "ref_unit_type") {
      //----- computed ref_unit_type --------------
      let ref_unit_type = "";
      if (props.parameter[ParamField.unitType] === "ref_unit_type") {
        const param = modbusStore.params.find(
          (param) =>
            param[ParamField.mbusAddress] ===
            props.parameter[ParamField.unit].toString(),
        );
        //------- added (for first)-------------------
        while (
          modbusStore.modbusValues[props.parameter[ParamField.unit]] ==
          undefined
        ) {
          await delay(10);
        }
        //---------------------------------
        ref_unit_type = param
          ? param.input_info.split("|")[
              modbusStore.modbusValues[props.parameter[ParamField.unit]]
            ]
          : "xxxxxxxxxxx";
      } else {
        ref_unit_type = props.parameter[ParamField.unitType] ?? "";
      }
      //---------------------------------------------------------
      const guiItem = modbusStore.guiUnits.find(
        (item) =>
          parseInt(item["No."]) ==
            modbusStore.modbusValues[props.parameter[ParamField.mbusAddress]] &&
          item.unit_type == ref_unit_type,
      );
      fieldValue.value = guiItem
        ? options.value.indexOf(guiItem["Symbol"] + " - " + guiItem.name)
        : "";
      modbusStore.modbusRefUnit[props.parameter[ParamField.mbusAddress]] =
        guiItem ? guiItem["Symbol"] : "";
    } else if (props.parameter[ParamField.unitType] === "default_unit") {
      const guiItem = modbusStore.guiUnits.find(
        (item) =>
          parseInt(item["No."]) ==
            modbusStore.modbusValues[props.parameter[ParamField.mbusAddress]] &&
          item.unit_type == props.parameter[ParamField.unit],
      );
      fieldValue.value = guiItem
        ? options.value.indexOf(guiItem["Symbol"] + " - " + guiItem.name)
        : "";
    } else {
      if (props.parameter.type == "uint16_t") {
        fieldValue.value =
          modbusStore.modbusValues[props.parameter[ParamField.mbusAddress]];
      }
      if (props.parameter.type == "uint32_t") {
        const regH = uint16_to_bytes(
          modbusStore.modbusValues[props.parameter[ParamField.mbusAddress]],
        );
        const regL = uint16_to_bytes(
          modbusStore.modbusValues[
            parseInt(props.parameter[ParamField.mbusAddress]) + 1
          ],
        );
        fieldValue.value = bytes_to_uint32([
          regH[0],
          regH[1],
          regL[0],
          regL[1],
        ]);
      }
    }
  }
}

function writeModbus() {
  modbusStore.modbusWrite(
    modbusStore.deviceAddress,
    props.parameter[ParamField.mbusAddress],
    modbusStore.modbusAddrLength[props.parameter[ParamField.mbusAddress]],
    new Uint8Array(write_value_array),
  );
}

function writeValue(write_value: number) {
  modbusStore.modbusIsChanged = false;
  if (props.parameter[ParamField.inputInfo] === "ref_unit_type") {
    const guiItem = modbusStore.guiUnits.find(
      (item) =>
        item.name === options.value[write_value].split(" - ")[1] &&
        item.unit_type === ref_unit_type.value,
    );
    const select_value = guiItem ? parseInt(guiItem["No."]) : 0;
    const reg = uint16_to_bytes(select_value);
    write_value_array = Array.from(reg);
    writeModbus();
  } else if (props.parameter[ParamField.unitType] === "default_unit") {
    const guiItem = modbusStore.guiUnits.find(
      (item) =>
        item.name === options.value[write_value].split(" - ")[1] &&
        item.unit_type === props.parameter[ParamField.unit],
    );
    const select_value = guiItem ? parseInt(guiItem["No."]) : 0;
    const reg = uint16_to_bytes(select_value);
    write_value_array = Array.from(reg);
    writeModbus();
  } else {
    if (props.parameter.type === "uint16_t") {
      const reg = uint16_to_bytes(write_value);
      write_value_array = Array.from(reg);
      writeModbus();
    }
    if (props.parameter.type === "uint32_t") {
      const reg = uint32_to_bytes(write_value);
      write_value_array = Array.from(reg);
      writeModbus();
    }
  }
}

function reloadValue() {
  reloadCount = 0;
  reload();
}

function reload() {
  if (modbusStore.storeInit && modbusStore.portState === PortState.connected) {
    if (modbusReqTimer.value != 0) {
      clearInterval(modbusReqTimer.value);
    }
    //-------- Device Info -------------------------
    modbusStore.modbusRead(modbusStore.deviceAddress, reload_addr, reload_len);
  }
}

function setDefault() {
  writeValue(parseInt(props.parameter[ParamField.default]));
}

watch(modbusAddrWriteBusy, (newVal: boolean, oldVal: boolean) => {
  if (
    newVal === false &&
    oldVal !== undefined &&
    write_value_array.length > 0
  ) {
    if (
      modbusStore.modbusAddrErrMsg[props.parameter[ParamField.mbusAddress]] ===
      ""
    ) {
      write_count = 0;
    } else {
      if (write_count < modbusStore.maxReloadCount) {
        writeModbus();
        write_count++;
      }
    }
  }
});

watch(isLoading, async (newVal) => {
  if (!newVal) {
    updateValue();
  }
});

watch(modbusAddrReadBusy, (newVal) => {
  if (!newVal) {
    if (
      modbusStore.modbusAddrErrMsg[props.parameter[ParamField.mbusAddress]] ==
      ""
    ) {
      reloadCount = 0;
    } else {
      if (reloadCount < modbusStore.maxReloadCount) {
        reload();
        reloadCount++;
      }
    }
  }
});

watch(selectOptions, (newVal) => {
  if (
    props.parameter[ParamField.unitType] === "ref_unit_type" &&
    props.parameter[ParamField.inputInfo] === "ref_unit_type"
  ) {
    // When the values are initially loaded, we don't want to immediately
    // adjust the value for the Unit (which would unexpectedly be written
    // to the EQ-DA). Therefore, skip execution of the watcher once.
    if (!isInitialized.value) {
      isInitialized.value = true;
      return;
    }

    fieldValue.value = 1;
    write_value_array = [0x00, 0x01];
    writeModbus();
    const show_unit = newVal[fieldValue.value]?.title.split(" - ")[0];
    if (show_unit) {
      const guiItem = modbusStore.guiUnits.find(
        (item) => item.Symbol == show_unit,
      );
      modbusStore.modbusRefUnit[props.parameter[ParamField.mbusAddress]] =
        guiItem ? guiItem["Symbol"] : "";
    }
  }
});

onMounted(() => {
  if (props.modbusReload) {
    modbusReqTimer.value = setInterval(async () => {
      if (
        modbusStore.storeInit &&
        modbusStore.portState === PortState.connected
      ) {
        //-------- Device Info -------------------------
        if (
          props.parameter[ParamField.inputInfo] === "ref_unit_type" &&
          props.parameter[ParamField.unitType] === "ref_unit_type" &&
          !(props.parameter[ParamField.unit] in modbusStore.modbusValues)
        ) {
          reload_addr = props.parameter[ParamField.unit];
          reload_len = 1;
        }
        if (props.parameter.type === "uint16_t") {
          reload_addr = props.parameter[ParamField.mbusAddress];
          reload_len = 1;
        }
        if (props.parameter.type === "uint32_t") {
          reload_addr = props.parameter[ParamField.mbusAddress];
          reload_len = 2;
        }
        reload();
        clearInterval(modbusReqTimer.value);
      }
    }, 200);
  } else {
    updateValue();
  }
});

onUnmounted(() => {
  if (modbusReqTimer.value) {
    clearInterval(modbusReqTimer.value);
  }
});
</script>
